// src/sectors/pilates/staff/modal/staffEditModal/StaffEditModal.jsx
import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import Card from '@mui/material/Card';
import Tooltip from '@mui/material/Tooltip';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';

export const WORK_TYPES = [
  { v: 'FULL_TIME', l: 'Tam Zamanlı (Maaş)' },
  { v: 'FULL_TIME_BONUS', l: 'Maaş + Prim' }, // ✅ NEW
  { v: 'HOURLY', l: 'Saatlik' },
  { v: 'HOURLY_BONUS', l: 'Saatlik + Prim' }, // ✅ NEW
  { v: 'PERCENT', l: 'Yüzde' },
];

const STAFF_ROLES = [
  { v: 'ADMIN', l: 'Yönetici' },
  { v: 'STAFF', l: 'Personel' },
];

const DAYS = [
  { v: 'MONDAY', l: 'Pzt' },
  { v: 'TUESDAY', l: 'Sal' },
  { v: 'WEDNESDAY', l: 'Çar' },
  { v: 'THURSDAY', l: 'Per' },
  { v: 'FRIDAY', l: 'Cum' },
  { v: 'SATURDAY', l: 'Cmt' },
  { v: 'SUNDAY', l: 'Paz' },
];
const DAY_LABEL = {
  MONDAY: 'Pzt',
  TUESDAY: 'Sal',
  WEDNESDAY: 'Çar',
  THURSDAY: 'Per',
  FRIDAY: 'Cum',
  SATURDAY: 'Cmt',
  SUNDAY: 'Paz',
};

function offDaysSummary(offDays = []) {
  if (!offDays.length) return 'Tatil günü yok (her gün çalışıyor)';
  return `Tatil: ${offDays.map((d) => DAY_LABEL[d] || d).join(', ')}`;
}

function SoftIcon({ icon, tone = 'teal' }) {
  const theme = useMuiTheme();
  const tones = {
    teal: { bg: 'rgba(0,150,136,.14)', fg: '#009688' },
    violet: { bg: 'rgba(124,58,237,.14)', fg: '#7c3aed' },
    amber: { bg: 'rgba(255,122,0,.14)', fg: '#ff7a00' },
  };
  const t = tones[tone] || tones.teal;
  return (
    <MDBox
      sx={{
        width: 44,
        height: 44,
        borderRadius: '16px',
        display: 'grid',
        placeItems: 'center',
        background: t.bg,
        color: t.fg,
        border: `1px solid rgba(0,0,0,.06)`,
        flex: '0 0 auto',
      }}
    >
      <Icon sx={{ fontSize: 22 }}>{icon}</Icon>
    </MDBox>
  );
}

function FieldCard({ icon, tone, title, subtitle, children }) {
  const theme = useMuiTheme();
  return (
    <Card
      sx={{
        p: { xs: 1.25, sm: 1.5 },
        borderRadius: '16px',
        boxShadow: 'none',
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <MDBox display="flex" alignItems="center" gap={1.2} sx={{ mb: 1 }}>
        <SoftIcon icon={icon} tone={tone} />
        <MDBox sx={{ minWidth: 0, display: 'grid', gap: 0.35 }}>
          {' '}
          <MDTypography
            variant="button"
            fontWeight="bold"
            sx={{ lineHeight: 1.1, color: theme.palette.text.primary }}
          >
            {title}
          </MDTypography>
          {subtitle ? (
            <MDTypography
              variant="caption"
              sx={{
                color: theme.palette.text.primary,
                opacity: 0.72, // ✅ soluk gri yerine net “siyah-gri”
                fontWeight: 500,
              }}
            >
              {subtitle}
            </MDTypography>
          ) : null}
        </MDBox>
      </MDBox>

      <MDBox sx={{ display: 'grid', gap: 1 }}>{children}</MDBox>
    </Card>
  );
}

const emptyForm = () => ({
  fullName: '',
  phone: '',
  email: '',
  birthDate: '',

  staffRole: 'STAFF',
  workType: 'FULL_TIME',

  monthlySalary: '',
  hourlyRate: '',
  percentageRate: '',
  bonusAmount: '', // ✅ NEW

  includedInPayroll: true,
  offDays: [],
});

function toFormModel(staff) {
  if (!staff) return emptyForm();

  const offDaysArr = Array.isArray(staff.offDays)
    ? staff.offDays
    : staff.offDays && typeof staff.offDays === 'object'
    ? Array.from(staff.offDays)
    : [];

  return {
    ...emptyForm(),
    fullName: staff.fullName ?? '',
    phone: staff.phone ?? '',
    email: staff.email ?? '',
    birthDate: staff.birthDate ?? '',
    staffRole: staff.staffRole ?? 'STAFF',
    workType: staff.workType ?? 'FULL_TIME',
    monthlySalary: staff.monthlySalary ?? '',
    hourlyRate: staff.hourlyRate ?? '',
    percentageRate: staff.percentageRate ?? '',
    bonusAmount: staff.bonusAmount ?? '', // ✅ NEW
    includedInPayroll: staff.includedInPayroll ?? true,
    offDays: offDaysArr,
  };
}

function numOrNull(x) {
  if (x === '' || x == null) return null;
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
}

// ✅ CreateStaffRequestDto / UpdateStaffRequestDto (aynı shape)
function toUpsertDto(form) {
  const dto = {
    fullName: String(form.fullName || '').trim(),
    phone: form.phone ? String(form.phone).trim() : null,
    email: form.email ? String(form.email).trim() : null,
    birthDate: form.birthDate ? form.birthDate : null,

    staffRole: form.staffRole,
    workType: form.workType,

    monthlySalary: null,
    hourlyRate: null,
    percentageRate: null,
    bonusAmount: null, // ✅ NEW

    includedInPayroll: Boolean(form.includedInPayroll),
    offDays: Array.isArray(form.offDays) ? form.offDays : [],
  };

  if (form.workType === 'FULL_TIME' || form.workType === 'FULL_TIME_BONUS') {
    dto.monthlySalary = numOrNull(form.monthlySalary);
  }

  if (form.workType === 'HOURLY' || form.workType === 'HOURLY_BONUS') {
    dto.hourlyRate = numOrNull(form.hourlyRate);
  }

  if (form.workType === 'PERCENT') {
    dto.percentageRate = numOrNull(form.percentageRate);
  }

  if (form.workType === 'FULL_TIME_BONUS' || form.workType === 'HOURLY_BONUS') {
    dto.bonusAmount = numOrNull(form.bonusAmount);
  }

  return dto;
}

export function StaffEditModal({ open, staff, onClose, onSave }) {
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [form, setForm] = React.useState(emptyForm());
  const [localErr, setLocalErr] = React.useState(null);

  React.useEffect(() => {
    if (!open) return;
    setLocalErr(null);
    setForm(toFormModel(staff));
  }, [open, staff]);

  const isNew = !staff?.id;
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const canSave = String(form.fullName || '').trim().length >= 3;

  const saveBlockReason = React.useMemo(() => {
    const name = String(form.fullName || '').trim();
    if (name.length < 3) return 'Ad soyad en az 3 karakter olmalı.';
    if (!form.staffRole) return 'Rol seçmelisin.';
    if (!form.workType) return 'Çalışma tipi seçmelisin.';

    if (form.workType === 'FULL_TIME' && numOrNull(form.monthlySalary) == null)
      return 'Aylık maaş gir.';
    if (form.workType === 'HOURLY' && numOrNull(form.hourlyRate) == null)
      return 'Saatlik ücret gir.';
    if (form.workType === 'PERCENT' && numOrNull(form.percentageRate) == null)
      return 'Yüzde oranı gir.';

    if (form.workType === 'FULL_TIME_BONUS') {
      if (numOrNull(form.monthlySalary) == null) return 'Aylık maaş gir.';
      if (numOrNull(form.bonusAmount) == null) return 'Prim gir.';
    }
    if (form.workType === 'HOURLY_BONUS') {
      if (numOrNull(form.hourlyRate) == null) return 'Saatlik ücret gir.';
      if (numOrNull(form.bonusAmount) == null) return 'Prim gir.';
    }

    return null;
  }, [form]);

  const saveDisabled = Boolean(saveBlockReason) || !canSave;

  const onClickSave = () => {
    setLocalErr(null);
    const dto = toUpsertDto(form);

    if (!dto.fullName || dto.fullName.length < 3)
      return setLocalErr('Ad soyad en az 3 karakter olmalı.');
    if (!dto.staffRole) return setLocalErr('Rol seçmelisin.');
    if (!dto.workType) return setLocalErr('Çalışma tipi seçmelisin.');

    // salary validations (opsiyonel ama iyi)
    if (dto.workType === 'FULL_TIME' && dto.monthlySalary == null)
      return setLocalErr('Aylık maaş gir.');
    if (dto.workType === 'HOURLY' && dto.hourlyRate == null)
      return setLocalErr('Saatlik ücret gir.');
    if (dto.workType === 'PERCENT' && dto.percentageRate == null)
      return setLocalErr('Yüzde oranı gir.');
    if (dto.workType === 'FULL_TIME_BONUS') {
      if (dto.monthlySalary == null) return setLocalErr('Aylık maaş gir.');
      if (dto.bonusAmount == null) return setLocalErr('Prim gir.');
    }
    if (dto.workType === 'HOURLY_BONUS') {
      if (dto.hourlyRate == null) return setLocalErr('Saatlik ücret gir.');
      if (dto.bonusAmount == null) return setLocalErr('Prim gir.');
    }

    onSave(dto, { isNew });
  };

  const headerBg = 'linear-gradient(180deg, rgba(0,150,136,.16), rgba(0,150,136,.06))';
  const denseInputSx = {
    '& .MuiOutlinedInput-root': { minHeight: 40 },
    '& .MuiOutlinedInput-input': { padding: '8px 12px', fontSize: '0.9rem' },
    '& .MuiInputLabel-root': { fontWeight: 375 },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : '22px',
          border: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
          boxShadow: theme.shadows[12],
        },
      }}
    >
      <DialogTitle sx={{ p: 0, background: headerBg }}>
        <MDBox sx={{ px: 2, py: 1.6 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
            <MDBox display="flex" alignItems="center" gap={1.2} sx={{ minWidth: 0 }}>
              <SoftIcon icon={isNew ? 'person_add' : 'edit'} tone="teal" />
              <MDBox sx={{ minWidth: 0, display: 'grid', gap: 0.35 }}>
                {' '}
                <MDTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
                  {isNew ? 'Yeni Çalışan' : 'Çalışanı Düzenle'}
                </MDTypography>
                <MDBox display="flex" gap={1} mt={0.6} sx={{ flexWrap: 'wrap' }}>
                  <MDTypography
                    variant="caption"
                    sx={{ color: theme.palette.text.secondary, mt: 0.35, display: 'block' }}
                  >
                    {isNew ? 'Yeni personel kaydı' : `ID: ${staff?.id ?? '-'}`} •{' '}
                    {WORK_TYPES.find((x) => x.v === form.workType)?.l}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </MDBox>

            <MDButton
              variant="outlined"
              color="dark"
              onClick={onClose}
              sx={{ borderRadius: '14px' }}
            >
              Kapat
            </MDButton>
          </MDBox>
        </MDBox>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 2 }}>
        <MDBox sx={{ display: 'grid', gap: 1.25 }}>
          <FieldCard
            icon="badge"
            tone="teal"
            title="Kimlik & İletişim"
            subtitle="İsim, telefon, e-posta"
          >
            <MDInput
              sx={denseInputSx}
              label="Ad Soyad"
              value={form.fullName}
              onChange={(e) => set('fullName', e.target.value)}
            />
            <MDBox
              sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.25 }}
            >
              <MDInput
                sx={denseInputSx}
                label="Telefon"
                value={form.phone}
                onChange={(e) => set('phone', e.target.value)}
              />
              <MDInput
                sx={denseInputSx}
                label="E-posta"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
              />
            </MDBox>
            <MDInput
              sx={denseInputSx}
              label="Doğum tarihi (opsiyonel)"
              type="date"
              value={form.birthDate}
              onChange={(e) => set('birthDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </FieldCard>

          <FieldCard
            icon="work"
            tone="violet"
            title="Rol & Çalışma Tipi"
            subtitle="Ücret modelini belirler"
          >
            <MDBox
              sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.25 }}
            >
              <MDInput
                sx={denseInputSx}
                select
                label="Rol"
                value={form.staffRole}
                onChange={(e) => set('staffRole', e.target.value)}
              >
                {STAFF_ROLES.map((x) => (
                  <MenuItem key={x.v} value={x.v}>
                    {x.l}
                  </MenuItem>
                ))}
              </MDInput>

              <MDInput
                sx={denseInputSx}
                select
                label="Çalışma tipi"
                value={form.workType}
                onChange={(e) => {
                  const next = e.target.value;
                  setForm((s) => ({
                    ...s,
                    workType: next,
                    monthlySalary: '',
                    hourlyRate: '',
                    percentageRate: '',
                    bonusAmount: '',
                  }));
                }}
              >
                {WORK_TYPES.map((x) => (
                  <MenuItem key={x.v} value={x.v}>
                    {x.l}
                  </MenuItem>
                ))}
              </MDInput>
            </MDBox>

            {form.workType === 'FULL_TIME' || form.workType === 'FULL_TIME_BONUS' ? (
              <MDBox
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: form.workType === 'FULL_TIME_BONUS' ? '1fr 1fr' : '1fr',
                  },
                  gap: 1.25,
                }}
              >
                <MDInput
                  sx={denseInputSx}
                  label="Aylık Maaş (₺)"
                  type="number"
                  value={form.monthlySalary}
                  onChange={(e) => set('monthlySalary', e.target.value)}
                />

                {form.workType === 'FULL_TIME_BONUS' ? (
                  <MDInput
                    sx={denseInputSx}
                    label="Prim (₺)"
                    type="number"
                    value={form.bonusAmount}
                    onChange={(e) => set('bonusAmount', e.target.value)}
                  />
                ) : null}
              </MDBox>
            ) : null}

            {form.workType === 'HOURLY' || form.workType === 'HOURLY_BONUS' ? (
              <MDBox
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: form.workType === 'HOURLY_BONUS' ? '1fr 1fr' : '1fr',
                  },
                  gap: 1.25,
                }}
              >
                <MDInput
                  sx={denseInputSx}
                  label="Saatlik Ücret (₺)"
                  type="number"
                  value={form.hourlyRate}
                  onChange={(e) => set('hourlyRate', e.target.value)}
                />

                {form.workType === 'HOURLY_BONUS' ? (
                  <MDInput
                    sx={denseInputSx}
                    label="Prim (₺)"
                    type="number"
                    value={form.bonusAmount}
                    onChange={(e) => set('bonusAmount', e.target.value)}
                  />
                ) : null}
              </MDBox>
            ) : null}

            {form.workType === 'PERCENT' ? (
              <MDInput
                sx={denseInputSx}
                label="Yüzde Oranı (%)"
                type="number"
                value={form.percentageRate}
                onChange={(e) => set('percentageRate', e.target.value)}
              />
            ) : null}

            <MDBox sx={{ mt: 0.5 }}>
              <MDTypography
                variant="button"
                sx={{ color: theme.palette.text.primary, fontWeight: 700 }}
              >
                Tatil Günleri Seç
              </MDTypography>

              <MDTypography
                variant="caption"
                sx={{
                  color: theme.palette.text.primary,
                  opacity: 0.72,
                  mt: 0.25,
                  display: 'block',
                }}
              >
                {offDaysSummary(form.offDays)}
              </MDTypography>
            </MDBox>
            <MDBox sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              {DAYS.map((d) => {
                const on = form.offDays.includes(d.v);

                return (
                  <Chip
                    key={d.v}
                    label={d.l}
                    icon={on ? <Icon sx={{ fontSize: 18 }}>check</Icon> : null}
                    onClick={() =>
                      set(
                        'offDays',
                        on ? form.offDays.filter((x) => x !== d.v) : [...form.offDays, d.v]
                      )
                    }
                    sx={{
                      height: 30,
                      borderRadius: '12px',
                      fontWeight: 700,
                      px: 0.5,
                      backgroundColor: on ? 'rgba(17,24,39,.10)' : 'transparent',
                      borderColor: on ? 'rgba(17,24,39,.35)' : theme.palette.divider,
                      color: theme.palette.text.primary,
                      transition: 'all .15s ease',
                      '&:hover': {
                        backgroundColor: on ? 'rgba(17,24,39,.14)' : 'rgba(17,24,39,.06)',
                      },
                      '& .MuiChip-icon': { color: theme.palette.text.primary, ml: 0.25 },
                    }}
                    variant={on ? 'filled' : 'outlined'}
                  />
                );
              })}
            </MDBox>
          </FieldCard>

          {localErr ? (
            <MDBox
              sx={{
                p: 1,
                borderRadius: '12px',
                border: `1px solid ${theme.palette.error.main}`,
                background: 'rgba(244,67,54,.06)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Icon sx={{ color: theme.palette.error.main, fontSize: 18 }}>error</Icon>
              <MDTypography
                variant="button"
                sx={{ color: theme.palette.error.main, fontWeight: 700 }}
              >
                {localErr}
              </MDTypography>
            </MDBox>
          ) : null}
        </MDBox>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <MDBox display="flex" justifyContent="flex-end" gap={1.2}>
          <MDButton variant="outlined" color="dark" sx={{ borderRadius: '14px' }} onClick={onClose}>
            İptal
          </MDButton>

          <Tooltip title={saveDisabled ? saveBlockReason : 'Kaydet'} placement="top" arrow>
            <span>
              <MDButton
                variant="gradient"
                color="success"
                sx={{ borderRadius: '14px' }}
                disabled={saveDisabled}
                onClick={onClickSave}
              >
                <Icon sx={{ mr: 0.4, fontSize: 18 }}>save</Icon>
                {isNew ? 'Oluştur' : 'Kaydet'}
              </MDButton>
            </span>
          </Tooltip>
        </MDBox>

        {saveDisabled && saveBlockReason ? (
          <MDBox sx={{ px: 1, pb: 1 }}>
            <MDBox
              sx={{
                px: 1.1,
                py: 1,
                borderRadius: '12px',
                border: `1px dashed ${theme.palette.divider}`,
                background: 'rgba(17,24,39,.04)',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Icon sx={{ fontSize: 18, opacity: 0.75 }}>info</Icon>
              <MDTypography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                Kaydet için: <b style={{ color: theme.palette.text.primary }}>{saveBlockReason}</b>
              </MDTypography>
            </MDBox>
          </MDBox>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
